
import { useState, useEffect } from "react";
import { Heart } from "lucide-react";
import { Button } from "./button";

interface HeartButtonProps {
  initialCount: number;
  isLiked: boolean;
  onToggle: () => Promise<void>;
  size?: "sm" | "md" | "lg";
}

export function HeartButton({ 
  initialCount, 
  isLiked: initialIsLiked, 
  onToggle, 
  size = "md" 
}: HeartButtonProps) {
  const [count, setCount] = useState(initialCount);
  const [isLiked, setIsLiked] = useState(initialIsLiked);
  const [isLoading, setIsLoading] = useState(false);

  // Update internal state when props change
  useEffect(() => {
    setCount(initialCount);
    setIsLiked(initialIsLiked);
  }, [initialCount, initialIsLiked]);

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (isLoading) return;
    
    // Optimistic update
    setIsLiked(prev => !prev);
    setCount(prev => isLiked ? prev - 1 : prev + 1);
    setIsLoading(true);
    
    try {
      await onToggle();
    } catch (error) {
      // Revert on error
      console.error("Error toggling like:", error);
      setIsLiked(prev => !prev);
      setCount(prev => !isLiked ? prev - 1 : prev + 1);
    } finally {
      setIsLoading(false);
    }
  };

  const sizeClasses = {
    sm: "h-6 px-1 gap-1 text-xs",
    md: "h-8 px-2 gap-1.5",
    lg: "h-10 px-3 gap-2"
  };
  
  const iconSizes = {
    sm: "h-3.5 w-3.5",
    md: "h-4 w-4",
    lg: "h-5 w-5"
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      className={`${sizeClasses[size]} hover:bg-transparent hover:text-primary ${isLiked ? 'text-rose-500' : 'text-muted-foreground'}`}
      onClick={handleClick}
      disabled={isLoading}
    >
      <Heart
        className={`${iconSizes[size]} ${isLiked ? 'fill-rose-500' : ''}`}
      />
      <span>{count}</span>
    </Button>
  );
}
